import React from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import ChipOverlay from '@ashn/component-library-react/ChipOverlay';
import Skeleton from '@mui/material/Skeleton';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { useErrorBoundary } from 'react-error-boundary';
import { NavLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import { PictureHelperContentful } from '@ashn/component-library-react/ImageHelpers';
import { sendDataToAnalytics } from '../../utils/misFunctions';
import { useHealthyDealsDataContext } from '../HealthyDealsDataContextProvider/HealthyDealsData.context';
import DealsCardSkeleton from '../Skeletons/DealsCardSkeleton';

const getColorForType = type => {
	switch (type.toLowerCase()) {
		case 'limited':
			return 'error.main'; // Assuming you would like to reference the theme as well
		case 'normal':
			return 'primary.main'; // Replace 'defaultColor'
		default:
			return null;
	}
};

const DealsCard = ({
	title = null,
	description = null,
	image = null,
	chipLabel = null,
	discountBadgeType = null,
	loading = false,
	cardLink = null,
	eventName = null,
	eventData = null,
}) => {
	const { prevPageTitle } = useHealthyDealsDataContext();
	const { showBoundary } = useErrorBoundary();

	if (loading) {
		return (
			<div data-testid="skeletonLoading">
				<DealsCardSkeleton />
			</div>
		);
	}

	const discountBadgeColor = getColorForType(discountBadgeType);

	return (
		<Link
			component={NavLink}
			underline="none"
			to={cardLink}
			onClick={e => {
				try {
					sendDataToAnalytics({ eventName, ...(eventData && { eventData }) });
					if (typeof cardLink !== 'string') {
						cardLink();
					}
				} catch (error) {
					e.preventDefault();
					showBoundary(error);
				}
			}}
			state={{ title: prevPageTitle }}
		>
			<Card variant="outlined" sx={{ height: '100%' }}>
				<Box>
					<ChipOverlay
						chipBackgroundColor={discountBadgeColor}
						chipLabel={chipLabel}
					>
						<PictureHelperContentful
							image={image}
							altText={image.altText ?? title}
						/>
					</ChipOverlay>
				</Box>
				<CardContent>
					<Typography sx={{ wordWrap: 'break-word' }}>
						<strong>{title}</strong>
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ wordWrap: 'break-word' }}
					>
						{description}
					</Typography>
				</CardContent>
			</Card>
		</Link>
	);
};

DealsCard.propTypes = {
	title: PropTypes.string,
	description: PropTypes.string,
	image: PropTypes.object,
	chipLabel: PropTypes.string,
	discountBadgeType: PropTypes.string,
	loading: PropTypes.bool,
	cardLink: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
	eventName: PropTypes.string,
	eventData: PropTypes.object,
};

export default DealsCard;
